import { SEED, type FeatureId, type Person, type Term, type Threat } from './data'
import { DEFAULT_ARCHITECTURE, type ArchitectureId } from './architecture'

/** Which of the four scripted flows the sidebar is parked on. */
export type Flow = 1 | 2 | 3 | 4

/** Screen showing on the manager's handset. */
export type ManagerScreen =
  | 'lock'
  | 'connect'
  | 'protect'
  | 'monitor'
  | 'profile'
  | 'sub'
  | 'term'
  | 'checkout'
  | 'success'
  | 'invite'
  | 'alertsetup'
  /** Zero-knowledge pairing, available on both handsets. */
  | 'shareinvite'
  | 'scanner'
  | 'alerts'
  | 'member'

/** Screen showing on the member's handset. */
export type MemberScreen =
  | 'lock'
  /** Apple Mail, showing the invite email. */
  | 'mail'
  /** Messages, showing the shared invite link. */
  | 'messages'
  /** Nord Account sign-up, reached from either invite route. */
  | 'signup'
  | 'join'
  | 'home'
  | 'protect'
  | 'monitor'
  | 'profile'
  | 'alerts'
  /** Zero-knowledge pairing, available on both handsets. */
  | 'shareinvite'
  | 'scanner'
  | 'devreq'
  | 'devoff'
  | 'blocked'

/** Which notification, if any, is sitting on the member's lock screen. */
export type MemberPush = 'mail' | 'message' | 'device' | 'breach' | 'none'

/** Per-device sharing state the manager has driven from their side. */
export type DeviceState = 'off' | 'pending' | 'sharing' | 'paused' | 'gone'

/** Group features a member can opt into, listed on their profile. */
export type ShareFeatureId = 'web' | 'call' | 'remote' | 'monitor'

export type MemberShares = Record<ShareFeatureId, boolean>

/**
 * A device is "sharing" exactly when it has agreed to share something. The two
 * views of that — the member's per-feature toggles and the manager's per-device
 * status — are the same fact, so they are written together everywhere.
 */
export const SHARES_OFF: MemberShares = { web: false, call: false, remote: false, monitor: false }
export const SHARES_ON: MemberShares = { web: true, call: true, remote: true, monitor: true }
export const isSharingAnything = (shares: MemberShares) => Object.values(shares).some(Boolean)

export type MemberAlert = { threat: Threat; shared: boolean }

export type DemoState = {
  /** Which architecture the whole demo is being told under. */
  arch: ArchitectureId

  flow: Flow
  /** Term picked on the Group upgrade screen. */
  upTerm: Term

  m: ManagerScreen
  k: MemberScreen
  kPush: MemberPush

  joined: boolean
  deviceAsked: boolean
  deviceAccepted: boolean

  /** Vito has been invited (occupies the first non-owner seat). */
  inviteTarget: boolean
  invites: Person[]
  /** An invitee has been picked in the invite field. */
  picked: boolean
  lastInvited: string

  /** Manager-side alert feed. */
  alerts: Threat[]
  unread: number
  /** Threat currently shown full-screen on the member's handset. */
  blocked: Threat | null
  /** Threat sitting on the manager's lock screen as a push. */
  mPush: Threat | null

  /** What the manager has named the group. */
  groupName: string
  /** Non-null while the group name is being edited. */
  groupNameDraft: string | null
  /** Friendly names the manager has given members, keyed by member. */
  aliases: Record<string, string>
  /** Non-null while a member's alias is being edited. */
  aliasDraft: { key: string; value: string } | null
  /**
   * Names given to devices, keyed by the device's real name.
   *
   * The real name stays the key everywhere else — pairing state, alerts — so a
   * rename is a label someone chose, never a new identity for the handset.
   */
  deviceNames: Record<string, string>
  /** Non-null while a device is being renamed. */
  deviceNameDraft: { key: string; value: string } | null
  /** Pairing kept but muted — the pair survives, the alerts stop. */
  sharingPaused: boolean

  /** The OS share sheet is up over the manager's handset, and what it shares. */
  shareSheet: boolean
  shareSheetKind: 'invite' | 'alerts'

  memberKey: string
  devState: Record<string, DeviceState>
  /** Threat Protection toggles on the member's handset. */
  memberFeatures: Record<FeatureId, boolean>
  /**
   * Group features the member has opted into, from their own profile.
   * Survives a flow switch: flipping one is what sends you into Flow 3, so
   * resetting it there would hide the thing you just did.
   */
  memberShares: MemberShares

  toastM: string | null
  toastK: string | null

  kBlockCount: number
  kAlerts: MemberAlert[]
  /** Dark Web Monitor hits against the member's own account. */
  breaches: Threat[]
  kUnread: number

}

export const INITIAL: DemoState = {
  arch: DEFAULT_ARCHITECTURE,
  flow: 1,
  upTerm: 'y2',
  m: 'connect',
  k: 'lock',
  // Flow 1 has not sent an invite yet, so the member handset is quiet.
  kPush: 'none',
  joined: false,
  deviceAsked: false,
  deviceAccepted: false,
  inviteTarget: false,
  invites: [],
  picked: false,
  lastInvited: 'Vito',
  alerts: [SEED],
  unread: 0,
  blocked: null,
  mPush: null,
  shareSheet: false,
  shareSheetKind: 'invite',
  groupName: 'My group',
  groupNameDraft: null,
  aliases: {},
  aliasDraft: null,
  deviceNames: {},
  deviceNameDraft: null,
  sharingPaused: false,
  memberKey: 'self',
  devState: {},
  memberFeatures: { maliciousSite: true, scamCall: true, adTracker: true },
  memberShares: SHARES_OFF,
  toastM: null,
  toastK: null,
  kBlockCount: 0,
  kAlerts: [],
  breaches: [],
  kUnread: 0,
}

/** Everything the "Reset demo" button clears. The architecture persists. */
export const RESETTABLE: Omit<DemoState, 'arch'> = {
  flow: INITIAL.flow,
  upTerm: INITIAL.upTerm,
  m: INITIAL.m,
  k: INITIAL.k,
  kPush: INITIAL.kPush,
  joined: INITIAL.joined,
  deviceAsked: INITIAL.deviceAsked,
  deviceAccepted: INITIAL.deviceAccepted,
  inviteTarget: INITIAL.inviteTarget,
  invites: INITIAL.invites,
  picked: INITIAL.picked,
  lastInvited: INITIAL.lastInvited,
  alerts: INITIAL.alerts,
  unread: INITIAL.unread,
  blocked: INITIAL.blocked,
  mPush: INITIAL.mPush,
  shareSheet: INITIAL.shareSheet,
  shareSheetKind: INITIAL.shareSheetKind,
  groupName: INITIAL.groupName,
  groupNameDraft: INITIAL.groupNameDraft,
  aliases: INITIAL.aliases,
  aliasDraft: INITIAL.aliasDraft,
  deviceNames: INITIAL.deviceNames,
  deviceNameDraft: INITIAL.deviceNameDraft,
  sharingPaused: INITIAL.sharingPaused,
  memberKey: INITIAL.memberKey,
  devState: INITIAL.devState,
  memberFeatures: INITIAL.memberFeatures,
  memberShares: INITIAL.memberShares,
  toastM: INITIAL.toastM,
  toastK: INITIAL.toastK,
  kBlockCount: INITIAL.kBlockCount,
  kAlerts: INITIAL.kAlerts,
  breaches: INITIAL.breaches,
  kUnread: INITIAL.kUnread,
}

/** Tuning knobs the design doc exposed as editable props. */
export const CONFIG = {
  /** Device name the demo triggers fire from. */
  memberDevice: "Vito's iPhone",
  /** How long a shared alert takes to reach the manager's handset. */
  alertDelayMs: 900,
  toastMs: 3600,
} as const
